import React, { useState, useEffect } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "../style/SignUp.css";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { registerUser, clearError } from "../store/authSlice";
import apiService from "../services/api";
import { RegisterRequest } from "../types/auth";

interface SignUpProps {
  onSignUpSuccess?: () => void;
  onBackToLogin?: () => void;
}

const SignUp: React.FC<SignUpProps> = ({ onSignUpSuccess, onBackToLogin }) => {
  const dispatch = useAppDispatch();
  const { isLoading, error, isAuthenticated } = useAppSelector((state) => state.auth);
  
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  useEffect(() => {
    localStorage.removeItem("email");
    localStorage.removeItem("password");
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated && onSignUpSuccess) {
      onSignUpSuccess();
    }
  }, [isAuthenticated, onSignUpSuccess]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    workEmail: "",
    phoneNumber: "",
    companyName: "",
    title: "",
    password: "",
    repeatPassword: "",
  });
  const [isError, setIsError] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [smsConsent, setSmsConsent] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleRepeatPasswordVisibility = () => {
    setShowRepeatPassword(!showRepeatPassword);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsError(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otpSent) {
      // Step 1: Send OTP
      if (!smsConsent) {
        setOtpError("Please confirm SMS consent to continue.");
        return;
      }
      try {
        await apiService.requestEmailOtp(formData.workEmail);
        setOtpSent(true);
        setOtpError(null);
      } catch (err) {
        setOtpError("Failed to send verification code. Please try again.");
      }
      return;
    }

    // After OTP is sent, validate the OTP then proceed
    if (formData.password !== formData.repeatPassword) {
      setIsError(true);
      return;
    }

    setIsError(false);

    try {
      const res = await apiService.verifyEmailOtp(formData.workEmail, otp.trim());
      if (!res?.verified) {
        setOtpError("Invalid or expired verification code.");
        return;
      }
    } catch (err) {
      setOtpError("Invalid or expired verification code.");
      return;
    }

    const registerData: RegisterRequest = {
      email: formData.workEmail,
      password: formData.password,
      firstName: formData.firstName,
      lastName: formData.lastName,
      companyName: formData.companyName,
      title: formData.title,
      phoneNumber: formData.phoneNumber,
    };

    try {
      await dispatch(registerUser(registerData)).unwrap();
    } catch (error) {
      console.error('Registration failed:', error);
    }
  };

  const isButtonDisabled = !otpSent
    ? (!formData.firstName ||
      !formData.lastName ||
      !formData.workEmail ||
      !formData.phoneNumber ||
      !formData.companyName ||
      !formData.title ||
      !formData.password ||
      !formData.repeatPassword ||
      !smsConsent)
    : (!otp || otp.length < 4);

  return (
    <div className="flex justify-center items-center login-form-width">
      <div className="w-[444px] p-6 rounded-2xl border border-lighterGray login-box-shadow h-fit">
        <h2 className="text-h1 font-bold text-darkestSlate pb-2">Sign Up</h2>
        <div className="flex items-center gap-1">
          <span className="text-h3 text-darkGray">
            Already have an account?
          </span>
          <button
            type="button"
            className="text-h3 font-semibold text-darkBlue hover:underline"
            onClick={onBackToLogin}
          >
            Log In
          </button>
        </div>
        <div className="border-b border-lighterGray my-8"></div>
        <form
          className="flex flex-col space-y-4"
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          <div className="flex flex-row gap-4">
            <div>
              <label
                htmlFor="firstName"
                className="text-h4 font-medium text-darkGray pb-1 block"
              >
                First Name *
              </label>
              <input
                type="text"
                placeholder="First Name"
                id="firstName"
                autoComplete="off"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                className="h-10 px-3 py-2.5 rounded-xl bg-lightestGray border border-lighterGray w-full"
              />
            </div>

            <div>
              <label
                htmlFor="lastName"
                className="text-h4 font-medium text-darkGray pb-1 block"
              >
                Last Name *
              </label>
              <input
                type="text"
                placeholder="Last Name"
                id="lastName"
                autoComplete="off"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                className="h-10 px-3 py-2.5 rounded-xl bg-lightestGray border border-lighterGray w-full"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="workEmail"
              className="text-h4 font-medium text-darkGray pb-1 block"
            >
              Work Email *
            </label>
            <input
              type="email"
              placeholder="Work Email"
              id="signupWorkEmail"
              autoComplete="off"
              value={formData.workEmail}
              onChange={(e) => handleInputChange("workEmail", e.target.value)}
              className="h-10 px-3 py-2.5 rounded-xl bg-lightestGray border border-lighterGray w-full"
            />
            {otpSent ? (
              <div className="mt-2">
                <label htmlFor="otp" className="text-h4 font-medium text-darkGray pb-1 block">Enter Verification Code</label>
                <input
                  type="text"
                  id="otp"
                  placeholder="6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="h-10 px-3 py-2.5 rounded-xl bg-lightestGray border border-lighterGray w-full"
                />
                {otpError && <p className="text-red-500 text-sm mt-1">{otpError}</p>}
                <button type="button" className="text-h4 font-semibold text-darkBlue hover:underline mt-2" onClick={async () => {
                  try {
                    await apiService.requestEmailOtp(formData.workEmail);
                    setOtpError(null);
                  } catch {
                    setOtpError("Failed to resend code. Try again in a moment.");
                  }
                }}>Resend code</button>
              </div>
            ) : null}
          </div>

          <div>
            <label
              htmlFor="phoneNumber"
              className="text-h4 font-medium text-darkGray pb-1 block"
            >
              Phone Number *
            </label>
            <PhoneInput
              international
              defaultCountry="US"
              value={formData.phoneNumber}
              onChange={(value) =>
                handleInputChange("phoneNumber", value || "")
              }
              placeholder="Phone Number"
            />
            <label className="mt-2 flex items-start justify-start gap-2 text-[10px] text-mediumGray select-none">
              <input
                type="checkbox"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
              />
              <span>
                I agree to receive text messages from MyHomey Technologies, Inc.
                Msg & data rates may apply. Msg frequency varies. Reply STOP to cancel, HELP for help.
              </span>
            </label>
          </div>

          <div className="flex flex-row gap-4">
            <div>
              <label
                htmlFor="companyName"
                className="text-h4 font-medium text-darkGray pb-1 block"
              >
                Company Name *
              </label>
              <input
                type="text"
                placeholder="Company Name"
                id="companyName"
                autoComplete="off"
                value={formData.companyName}
                onChange={(e) =>
                  handleInputChange("companyName", e.target.value)
                }
                className="h-10 px-3 py-2.5 rounded-xl bg-lightestGray border border-lighterGray w-full"
              />
            </div>

            <div>
              <label
                htmlFor="title"
                className="text-h4 font-medium text-darkGray pb-1 block"
              >
                Title *
              </label>
              <input
                type="text"
                placeholder="Title"
                id="title"
                autoComplete="off"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                className="h-10 px-3 py-2.5 rounded-xl bg-lightestGray border border-lighterGray w-full"
              />
            </div>
          </div>

          <div className="flex flex-row gap-4">
            <div className="flex-1">
              <label
                htmlFor="password"
                className="text-h4 font-medium text-darkGray pb-1 block"
              >
                Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  id="signupPassword"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  className="h-10 px-3 py-2.5 rounded-xl bg-lightestGray border border-lighterGray w-full pr-20"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-2">
                  <span
                    className="cursor-pointer"
                    onClick={togglePasswordVisibility}
                  >
                    <i
                      className={`fa-regular ${
                        showPassword ? "fa-eye" : "fa-eye-slash"
                      } text-darkGray`}
                    ></i>
                  </span>
                </div>
              </div>
            </div>

            <div className="flex-1">
              <label
                htmlFor="repeatPassword"
                className="text-h4 font-medium text-darkGray pb-1 block"
              >
                Repeat Password *
              </label>
              <div className="relative">
                <input
                  type={showRepeatPassword ? "text" : "password"}
                  placeholder="Repeat Password"
                  id="signupRepeatPassword"
                  autoComplete="new-password"
                  value={formData.repeatPassword}
                  onChange={(e) =>
                    handleInputChange("repeatPassword", e.target.value)
                  }
                  className="h-10 px-3 py-2.5 rounded-xl bg-lightestGray border border-lighterGray w-full pr-10"
                />
                <span
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                  onClick={toggleRepeatPasswordVisibility}
                >
                  <i
                    className={`fa-regular ${
                      showRepeatPassword ? "fa-eye" : "fa-eye-slash"
                    } text-darkGray`}
                  ></i>
                </span>
              </div>
            </div>
          </div>

          {isError && (
            <p className="text-red-500 text-sm">Passwords do not match</p>
          )}

          {error && (
            <p className="text-red-500 text-sm">{error}</p>
          )}

          <button
            disabled={isButtonDisabled || isLoading}
            type="submit"
            className={`text-h3 font-semibold text-lightestGray h-10 rounded-3xl hover:bg-mediumGray mt-4 ${
              isButtonDisabled || isLoading ? "bg-gray cursor-not-allowed" : "bg-darkBlue"
            }`}
          >
            {isLoading ? "Creating Account..." : (!otpSent ? "Send Verification Code" : "Verify & Create Account")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SignUp;
